import {
  Button,
  Input,
  Menu,
  MenuItem,
  MenuTrigger,
  Slider,
} from '@affine/component';
import {
  SettingHeader,
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { formatMailDate } from '@affine/core/modules/streamlain/mail/types';
import {
  useMailSearchMaintenance,
  useWorkspaceMailAdmin,
} from '@affine/core/modules/streamlain/mail/use-workspace-mail';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { isBackendUnavailableError } from '@affine/core/utils/backend-availability';
import type {
  MailPolicyPreset,
  MailRetentionMode,
  MailSearchIndexStatus,
} from '@affine/graphql';
import { MailPolicyPreset as MailPolicyPresetValue } from '@affine/graphql';
import { MailRetentionMode as MailRetentionModeValue } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { type FC, useEffect, useMemo, useState } from 'react';

import {
  SettingsOfflineHeaderAction,
  useSettingsOfflineIncompleteLabel,
} from '../../shared/settings-offline-status';
import {
  SettingsConfirmButton,
  SettingsContentSurface,
  SettingsErrorBanner,
  SettingsNotice,
  SettingsTabbedContent,
} from '../shared/settings-content';
import {
  SettingsBadge,
  SettingsEntityAvatar,
  SettingsEntityList,
  SettingsEntityMessage,
  SettingsExpandableItem,
  SettingsMetaItem,
} from '../shared/settings-entity';
import { SettingsListControls } from '../shared/settings-navigation';
import * as styles from './index.css';

interface SelectOption<T extends string> {
  value: T;
  label: string;
}

const BYTES_PER_MB = 1024 * 1024;
const BYTES_PER_KB = 1024;
const COUNT_FORMATTER = new Intl.NumberFormat('de-DE');
const WORKSPACE_MAIL_I18N_PREFIX = 'com.streamlain.settings.workspace.mail';
const PRESET_ORDER: ReadonlyArray<MailPolicyPreset> = [
  MailPolicyPresetValue.eco,
  MailPolicyPresetValue.standard,
  MailPolicyPresetValue.pro,
  MailPolicyPresetValue.custom,
];

type MailAdminOverview = NonNullable<
  ReturnType<typeof useWorkspaceMailAdmin>['overview']
>;
type MailAccountOverview = MailAdminOverview['accounts'][number];
type MailQuotaUserOverview = MailAdminOverview['quotaUsers'][number];
type MailQuotaFactorOverview = MailQuotaUserOverview['factors'][number];
type MailPolicyPresetCatalogItem = MailAdminOverview['presetCatalog'][number];
type MailPolicyCustomBounds = MailAdminOverview['customBounds'];
type MailSearchDiagnostics = ReturnType<
  typeof useMailSearchMaintenance
>['diagnostics'];
type MailSearchAdminStatus = MailAdminOverview['searchIndexStatus'];
type MailUserTarget = {
  userId: string;
  label: string;
  userEmail: string;
  accountDetail: string;
  budgetMaxMb: number;
  accounts: MailAccountOverview[];
  latestSyncedAt: string | null;
};

function workspaceMailText(
  i18n: ReturnType<typeof useI18n>,
  key: string,
  options?: Record<string, number | string>
) {
  return i18n.t(`${WORKSPACE_MAIL_I18N_PREFIX}.${key}`, options);
}

function retentionOptions(
  i18n: ReturnType<typeof useI18n>
): ReadonlyArray<SelectOption<MailRetentionMode>> {
  return [
    {
      value: MailRetentionModeValue.none,
      label: workspaceMailText(i18n, 'retention.none'),
    },
    {
      value: MailRetentionModeValue.days,
      label: workspaceMailText(i18n, 'retention.days'),
    },
  ];
}

function searchIndexOptions(
  i18n: ReturnType<typeof useI18n>
): ReadonlyArray<SelectOption<'enabled' | 'paused'>> {
  return [
    { value: 'enabled', label: workspaceMailText(i18n, 'search.enabled') },
    { value: 'paused', label: workspaceMailText(i18n, 'search.paused') },
  ];
}

function SettingsSelect<T extends string>({
  value,
  options,
  onChange,
  disabled,
}: {
  value: T;
  options: ReadonlyArray<SelectOption<T>>;
  onChange: (value: T) => void;
  disabled?: boolean;
}) {
  const current = options.find(option => option.value === value) ?? options[0];
  return (
    <Menu
      contentOptions={{ align: 'end' }}
      items={options.map(option => (
        <MenuItem
          key={option.value}
          onSelect={() => onChange(option.value)}
          data-selected={option.value === value}
        >
          {option.label}
        </MenuItem>
      ))}
    >
      <MenuTrigger className={styles.dropdownTrigger} block disabled={disabled}>
        {current?.label ?? ''}
      </MenuTrigger>
    </Menu>
  );
}

function RangeNumberControl({
  value,
  min,
  max,
  fallback,
  unit,
  disabled,
  onChange,
}: {
  value: number | '';
  min: number;
  max: number;
  fallback: number;
  unit: string;
  disabled?: boolean;
  onChange: (value: number | '') => void;
}) {
  const safeMax = Math.max(min, max);
  const numericValue = clampNumber(
    value === '' ? fallback : value,
    min,
    safeMax
  );
  return (
    <div className={styles.rangeControl}>
      <Slider
        min={min}
        max={safeMax}
        step={1}
        value={[numericValue]}
        width={180}
        disabled={disabled}
        onValueChange={next => {
          onChange(clampNumber(next[0] ?? fallback, min, safeMax));
        }}
      />
      <div className={styles.inputWithUnit}>
        <Input
          type="number"
          min={min}
          max={safeMax}
          value={String(value)}
          disabled={disabled}
          inputStyle={{ textAlign: 'right' }}
          onBlur={() => {
            onChange(numericValue);
          }}
          onChange={next => {
            if (!next.trim()) {
              onChange('');
              return;
            }
            onChange(clampNumber(Number(next), min, safeMax));
          }}
        />
        <span className={styles.unit}>{unit}</span>
      </div>
    </div>
  );
}

function QuotaProgress({
  percent,
  status,
}: {
  percent: number;
  status: string;
}) {
  const safePercent = Math.max(
    0,
    Math.min(100, Number.isFinite(percent) ? percent : 0)
  );
  return (
    <div className={styles.quotaProgress}>
      <div className={styles.quotaTrack}>
        <div
          className={styles.quotaFill}
          data-status={status}
          style={{ width: `${safePercent}%` }}
        />
      </div>
      <span className={styles.quotaValue}>
        {COUNT_FORMATTER.format(Math.round(percent))}%
      </span>
    </div>
  );
}

function QuotaFactorList({
  factors,
  i18n,
}: {
  factors: MailQuotaFactorOverview[];
  i18n: ReturnType<typeof useI18n>;
}) {
  return (
    <div className={styles.quotaFactorList}>
      {factors.map(factor => (
        <div className={styles.quotaFactorRow} key={factor.kind}>
          <div className={styles.rowText}>
            <span className={styles.rowLabel}>
              {formatQuotaFactorLabel(i18n, factor)}
            </span>
            <span className={styles.rowDesc}>
              {formatQuotaFactorDetail(i18n, factor)}
            </span>
          </div>
          <QuotaProgress percent={factor.percent} status={factor.status} />
        </div>
      ))}
    </div>
  );
}

function sortPresetCatalog(
  catalog: MailPolicyPresetCatalogItem[]
): MailPolicyPresetCatalogItem[] {
  return [...catalog].sort(
    (left, right) =>
      PRESET_ORDER.indexOf(left.preset) - PRESET_ORDER.indexOf(right.preset)
  );
}

function getPolicyPresetOption(
  catalog: MailPolicyPresetCatalogItem[],
  preset: MailPolicyPreset
): MailPolicyPresetCatalogItem | null {
  return (
    catalog.find(option => option.preset === preset) ??
    catalog.find(option => option.preset === MailPolicyPresetValue.custom) ??
    null
  );
}

function presetLabel(
  i18n: ReturnType<typeof useI18n>,
  preset: MailPolicyPreset
): string {
  return workspaceMailText(i18n, `preset.${preset}.label`);
}

function presetMeta(
  i18n: ReturnType<typeof useI18n>,
  preset: MailPolicyPreset
): string {
  return workspaceMailText(i18n, `preset.${preset}.meta`);
}

function formatPresetEffect(
  i18n: ReturnType<typeof useI18n>,
  preset: MailPolicyPresetCatalogItem | null,
  values: {
    searchIndexWindowDays: number | '';
    searchIndexMaxMessagesPerUser: number | '';
    bodyCacheTtlDays: number | '';
    attachmentCacheTtlDays: number | '';
    bodyCacheBudgetMb: number | '';
    attachmentCacheBudgetMb: number | '';
  },
  fallback: {
    searchIndexWindowDays: number;
    searchIndexMaxMessagesPerUser: number;
    bodyCacheTtlDays: number;
    attachmentCacheTtlDays: number;
    bodyCacheBudgetMb: number;
    attachmentCacheBudgetMb: number;
  }
): string {
  const searchDays = resolveNumber(
    preset?.searchIndexWindowDays,
    values.searchIndexWindowDays,
    fallback.searchIndexWindowDays
  );
  const messages = resolveNumber(
    preset?.searchIndexMaxMessagesPerUser,
    values.searchIndexMaxMessagesPerUser,
    fallback.searchIndexMaxMessagesPerUser
  );
  const bodyTtl = resolveNumber(
    preset?.bodyCacheTtlDays,
    values.bodyCacheTtlDays,
    fallback.bodyCacheTtlDays
  );
  const attachmentTtl = resolveNumber(
    preset?.attachmentCacheTtlDays,
    values.attachmentCacheTtlDays,
    fallback.attachmentCacheTtlDays
  );
  const bodyBudget = resolveNumber(
    preset?.bodyCacheBudgetBytes
      ? bytesToMb(preset.bodyCacheBudgetBytes)
      : null,
    values.bodyCacheBudgetMb,
    fallback.bodyCacheBudgetMb
  );
  const attachmentBudget = resolveNumber(
    preset?.attachmentCacheBudgetBytes
      ? bytesToMb(preset.attachmentCacheBudgetBytes)
      : null,
    values.attachmentCacheBudgetMb,
    fallback.attachmentCacheBudgetMb
  );

  return workspaceMailText(i18n, 'presetEffect', {
    searchDays: formatCount(searchDays),
    messages: formatCount(messages),
    bodyTtl: formatCount(bodyTtl),
    bodyBudget: formatCount(bodyBudget),
    attachmentTtl: formatCount(attachmentTtl),
    attachmentBudget: formatCount(attachmentBudget),
  });
}

function formatProviderCoverageNotice(
  i18n: ReturnType<typeof useI18n>,
  searchIndexWindowDays: number | '',
  fallbackDays: number,
  maxDays: number
) {
  const days =
    searchIndexWindowDays === '' ? fallbackDays : searchIndexWindowDays;
  return workspaceMailText(i18n, 'providerCoverageNotice', {
    days: formatCount(days),
    maxDays: formatCount(maxDays),
  });
}

export const WorkspaceSettingMail: FC = () => {
  const i18n = useI18n();
  const t = (key: string, options?: Record<string, number | string>): string =>
    workspaceMailText(i18n, key, options);
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;
  const mailAdmin = useWorkspaceMailAdmin(workspaceId);
  const searchMaintenance = useMailSearchMaintenance(workspaceId);
  const backendAvailable = useBackendAvailable();
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const overview = mailAdmin.overview;
  const diagnostics = searchMaintenance.diagnostics;
  const [policyPreset, setPolicyPreset] = useState<MailPolicyPreset>(
    MailPolicyPresetValue.standard
  );
  const [retentionMode, setRetentionMode] = useState<MailRetentionMode>(
    MailRetentionModeValue.none
  );
  const [retentionDays, setRetentionDays] = useState<number | ''>('');
  const [bodyCacheTtlDays, setBodyCacheTtlDays] = useState<number | ''>('');
  const [attachmentCacheTtlDays, setAttachmentCacheTtlDays] = useState<
    number | ''
  >('');
  const [bodyCacheBudgetMb, setBodyCacheBudgetMb] = useState<number | ''>('');
  const [attachmentCacheBudgetMb, setAttachmentCacheBudgetMb] = useState<
    number | ''
  >('');
  const [searchIndexMode, setSearchIndexMode] = useState<'enabled' | 'paused'>(
    'enabled'
  );
  const [searchIndexWindowDays, setSearchIndexWindowDays] = useState<
    number | ''
  >('');
  const [searchIndexMaxMessagesPerUser, setSearchIndexMaxMessagesPerUser] =
    useState<number | ''>('');
  const [searchIndexWorkspaceBudgetMb, setSearchIndexWorkspaceBudgetMb] =
    useState<number | ''>('');
  const [searchIndexUserBudgetMb, setSearchIndexUserBudgetMb] = useState<
    number | ''
  >('');
  const [searchIndexBodyKb, setSearchIndexBodyKb] = useState<number | ''>('');
  const [overrideUserId, setOverrideUserId] = useState('');
  const [overrideBodyBudgetMb, setOverrideBodyBudgetMb] = useState<number | ''>(
    ''
  );
  const [overrideAttachmentBudgetMb, setOverrideAttachmentBudgetMb] = useState<
    number | ''
  >('');
  const [overrideSearchQuery, setOverrideSearchQuery] = useState('');
  const [expandedAccountUserId, setExpandedAccountUserId] = useState<
    string | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingOverride, setIsSavingOverride] = useState(false);
  const [isRepairingSearch, setIsRepairingSearch] = useState(false);
  const [isRebuildingSearchIndex, setIsRebuildingSearchIndex] = useState(false);
  const [searchRepairResult, setSearchRepairResult] = useState<string | null>(
    null
  );
  const [searchIndexRebuildResult, setSearchIndexRebuildResult] = useState<
    string | null
  >(null);
  const policy = overview?.policy;
  const presetCatalog = useMemo(
    () => sortPresetCatalog(overview?.presetCatalog ?? []),
    [overview?.presetCatalog]
  );
  const customBounds = overview?.customBounds;
  const uiBounds = useMemo(
    () => toPolicyUiBounds(customBounds, overview?.cacheBudgetMaxBytes),
    [customBounds, overview?.cacheBudgetMaxBytes]
  );
  const policyFallbacks = useMemo(
    () => toPolicyFallbacks(policy, uiBounds),
    [policy, uiBounds]
  );
  const retentionSelectOptions = useMemo(() => retentionOptions(i18n), [i18n]);
  const searchIndexSelectOptions = useMemo(
    () => searchIndexOptions(i18n),
    [i18n]
  );
  const usage = overview?.usage;
  const searchIndexStatus = overview?.searchIndexStatus;
  const accounts = useMemo(
    () => overview?.accounts ?? [],
    [overview?.accounts]
  );
  const cacheUserOverrides = useMemo(
    () => overview?.cacheUserOverrides ?? [],
    [overview?.cacheUserOverrides]
  );
  const quotaUsers = useMemo(
    () => overview?.quotaUsers ?? [],
    [overview?.quotaUsers]
  );
  const quotaUserById = useMemo(
    () => new Map(quotaUsers.map(user => [user.userId, user])),
    [quotaUsers]
  );
  const mailUserTargets = useMemo(
    () => groupMailAccountsByUser(i18n, accounts),
    [accounts, i18n]
  );
  const cacheBudgetMaxMb = uiBounds.cacheBudgetMaxMb;
  const policyBodyBudgetMb = bytesToMb(
    policy?.bodyCacheBudgetBytes,
    policyFallbacks.bodyCacheBudgetMb
  );
  const policyAttachmentBudgetMb = bytesToMb(
    policy?.attachmentCacheBudgetBytes,
    policyFallbacks.attachmentCacheBudgetMb
  );
  const filteredCacheOverrideTargets = useMemo(
    () => filterCacheOverrideTargets(mailUserTargets, overrideSearchQuery),
    [mailUserTargets, overrideSearchQuery]
  );

  useEffect(() => {
    if (!policy) return;
    setPolicyPreset(policy.preset ?? MailPolicyPresetValue.custom);
    setRetentionMode(policy.retentionMode);
    setRetentionDays(policy.retentionDays ?? '');
    setBodyCacheTtlDays(policy.bodyCacheTtlDays);
    setAttachmentCacheTtlDays(policy.attachmentCacheTtlDays);
    setBodyCacheBudgetMb(bytesToMb(policy.bodyCacheBudgetBytes));
    setAttachmentCacheBudgetMb(bytesToMb(policy.attachmentCacheBudgetBytes));
    setSearchIndexMode(policy.searchIndexEnabled ? 'enabled' : 'paused');
    setSearchIndexWindowDays(policy.searchIndexWindowDays);
    setSearchIndexMaxMessagesPerUser(policy.searchIndexMaxMessagesPerUser);
    setSearchIndexWorkspaceBudgetMb(
      bytesToMb(policy.searchIndexMaxWorkspaceBytes)
    );
    setSearchIndexUserBudgetMb(bytesToMb(policy.searchIndexMaxUserBytes));
    setSearchIndexBodyKb(
      Math.round(policy.searchIndexBodyMaxBytes / BYTES_PER_KB)
    );
  }, [policy]);

  useEffect(() => {
    if (!overrideUserId) return;
    const override = cacheUserOverrides.find(
      row => row.userId === overrideUserId
    );
    const target = mailUserTargets.find(row => row.userId === overrideUserId);
    const nextBodyBudgetMb = override
      ? bytesToMb(
          override.bodyCacheBudgetBytes,
          policyFallbacks.bodyCacheBudgetMb
        )
      : target
        ? getDefaultUserCacheBudgetMb(
            target,
            policyBodyBudgetMb,
            uiBounds.bodyCacheBudgetMb.min
          )
        : policyFallbacks.bodyCacheBudgetMb;
    const nextAttachmentBudgetMb = override
      ? bytesToMb(
          override.attachmentCacheBudgetBytes,
          policyFallbacks.attachmentCacheBudgetMb
        )
      : target
        ? getDefaultUserCacheBudgetMb(
            target,
            policyAttachmentBudgetMb,
            uiBounds.attachmentCacheBudgetMb.min
          )
        : policyFallbacks.attachmentCacheBudgetMb;
    setOverrideBodyBudgetMb(current =>
      current === nextBodyBudgetMb ? current : nextBodyBudgetMb
    );
    setOverrideAttachmentBudgetMb(current =>
      current === nextAttachmentBudgetMb ? current : nextAttachmentBudgetMb
    );
  }, [
    cacheUserOverrides,
    mailUserTargets,
    overrideUserId,
    policyAttachmentBudgetMb,
    policyBodyBudgetMb,
    policyFallbacks.attachmentCacheBudgetMb,
    policyFallbacks.bodyCacheBudgetMb,
    uiBounds.attachmentCacheBudgetMb.min,
    uiBounds.bodyCacheBudgetMb.min,
  ]);

  const showOfflineNotice =
    !backendAvailable ||
    (!!mailAdmin.error && isBackendUnavailableError(mailAdmin.error)) ||
    (!!searchMaintenance.error &&
      isBackendUnavailableError(searchMaintenance.error));
  const header = (
    <SettingHeader
      title={t('title')}
      action={<SettingsOfflineHeaderAction show={showOfflineNotice} />}
    />
  );

  if (mailAdmin.isLoading) {
    return (
      <>
        {header}
        <SettingsTabbedContent>
          <SettingsEntityMessage>
            {showOfflineNotice ? offlineLabel : t('loading')}
          </SettingsEntityMessage>
        </SettingsTabbedContent>
      </>
    );
  }

  const savePolicy = async () => {
    setError(null);
    setIsSaving(true);
    const nextRetentionDays =
      retentionMode === MailRetentionModeValue.days
        ? clampNumber(
            retentionDays === ''
              ? policyFallbacks.retentionDays
              : retentionDays,
            uiBounds.retentionDays.min,
            uiBounds.retentionDays.max
          )
        : null;
    const nextBodyCacheTtlDays = clampNumber(
      bodyCacheTtlDays === ''
        ? policyFallbacks.bodyCacheTtlDays
        : bodyCacheTtlDays,
      uiBounds.bodyCacheTtlDays.min,
      uiBounds.bodyCacheTtlDays.max
    );
    const nextAttachmentCacheTtlDays = clampNumber(
      attachmentCacheTtlDays === ''
        ? policyFallbacks.attachmentCacheTtlDays
        : attachmentCacheTtlDays,
      uiBounds.attachmentCacheTtlDays.min,
      uiBounds.attachmentCacheTtlDays.max
    );
    const nextBodyCacheBudgetMb = clampNumber(
      bodyCacheBudgetMb === ''
        ? policyFallbacks.bodyCacheBudgetMb
        : bodyCacheBudgetMb,
      uiBounds.bodyCacheBudgetMb.min,
      Math.min(cacheBudgetMaxMb, uiBounds.bodyCacheBudgetMb.max)
    );
    const nextAttachmentCacheBudgetMb = clampNumber(
      attachmentCacheBudgetMb === ''
        ? policyFallbacks.attachmentCacheBudgetMb
        : attachmentCacheBudgetMb,
      uiBounds.attachmentCacheBudgetMb.min,
      Math.min(cacheBudgetMaxMb, uiBounds.attachmentCacheBudgetMb.max)
    );
    const nextSearchIndexWorkspaceBudgetMb = clampNumber(
      searchIndexWorkspaceBudgetMb === ''
        ? policyFallbacks.searchIndexWorkspaceBudgetMb
        : searchIndexWorkspaceBudgetMb,
      uiBounds.searchIndexWorkspaceBudgetMb.min,
      uiBounds.searchIndexWorkspaceBudgetMb.max
    );
    const nextSearchIndexUserBudgetMb = clampNumber(
      searchIndexUserBudgetMb === ''
        ? policyFallbacks.searchIndexUserBudgetMb
        : searchIndexUserBudgetMb,
      uiBounds.searchIndexUserBudgetMb.min,
      Math.min(
        uiBounds.searchIndexUserBudgetMb.max,
        nextSearchIndexWorkspaceBudgetMb
      )
    );
    try {
      await mailAdmin.updatePolicy({
        preset: policyPreset,
        retentionMode,
        retentionDays: nextRetentionDays,
        bodyCacheTtlDays: nextBodyCacheTtlDays,
        attachmentCacheTtlDays: nextAttachmentCacheTtlDays,
        bodyCacheBudgetBytes: String(
          Math.floor(nextBodyCacheBudgetMb * BYTES_PER_MB)
        ),
        attachmentCacheBudgetBytes: String(
          Math.floor(nextAttachmentCacheBudgetMb * BYTES_PER_MB)
        ),
        searchIndexEnabled: searchIndexMode === 'enabled',
        searchIndexWindowDays: clampNumber(
          searchIndexWindowDays === ''
            ? policyFallbacks.searchIndexWindowDays
            : searchIndexWindowDays,
          uiBounds.searchIndexWindowDays.min,
          uiBounds.searchIndexWindowDays.max
        ),
        searchIndexMaxMessagesPerUser: clampNumber(
          searchIndexMaxMessagesPerUser === ''
            ? policyFallbacks.searchIndexMaxMessagesPerUser
            : searchIndexMaxMessagesPerUser,
          uiBounds.searchIndexMaxMessagesPerUser.min,
          uiBounds.searchIndexMaxMessagesPerUser.max
        ),
        searchIndexMaxWorkspaceBytes: String(
          Math.floor(nextSearchIndexWorkspaceBudgetMb * BYTES_PER_MB)
        ),
        searchIndexMaxUserBytes: String(
          Math.floor(nextSearchIndexUserBudgetMb * BYTES_PER_MB)
        ),
        searchIndexBodyMaxBytes:
          clampNumber(
            searchIndexBodyKb === ''
              ? policyFallbacks.searchIndexBodyKb
              : searchIndexBodyKb,
            uiBounds.searchIndexBodyKb.min,
            uiBounds.searchIndexBodyKb.max
          ) * BYTES_PER_KB,
        searchIndexAttachmentsEnabled: false,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Policy update failed');
    } finally {
      setIsSaving(false);
    }
  };

  const selectedOverride = cacheUserOverrides.find(
    override => override.userId === overrideUserId
  );
  const selectedOverrideTarget = mailUserTargets.find(
    target => target.userId === overrideUserId
  );
  const overrideBudgetMaxMb =
    selectedOverrideTarget?.budgetMaxMb ?? cacheBudgetMaxMb;
  const selectedOverrideDefaultBodyMb = selectedOverrideTarget
    ? getDefaultUserCacheBudgetMb(
        selectedOverrideTarget,
        policyBodyBudgetMb,
        uiBounds.bodyCacheBudgetMb.min
      )
    : policyFallbacks.bodyCacheBudgetMb;
  const selectedOverrideDefaultAttachmentMb = selectedOverrideTarget
    ? getDefaultUserCacheBudgetMb(
        selectedOverrideTarget,
        policyAttachmentBudgetMb,
        uiBounds.attachmentCacheBudgetMb.min
      )
    : policyFallbacks.attachmentCacheBudgetMb;

  const saveCacheOverride = async () => {
    if (!overrideUserId) return;
    setError(null);
    setIsSavingOverride(true);
    const nextOverrideBodyBudgetMb = clampNumber(
      overrideBodyBudgetMb === ''
        ? selectedOverrideDefaultBodyMb
        : overrideBodyBudgetMb,
      uiBounds.bodyCacheBudgetMb.min,
      overrideBudgetMaxMb
    );
    const nextOverrideAttachmentBudgetMb = clampNumber(
      overrideAttachmentBudgetMb === ''
        ? selectedOverrideDefaultAttachmentMb
        : overrideAttachmentBudgetMb,
      uiBounds.attachmentCacheBudgetMb.min,
      overrideBudgetMaxMb
    );
    try {
      await mailAdmin.updateCacheUserOverride({
        workspaceId,
        userId: overrideUserId,
        bodyCacheBudgetBytes: String(
          Math.floor(nextOverrideBodyBudgetMb * BYTES_PER_MB)
        ),
        attachmentCacheBudgetBytes: String(
          Math.floor(nextOverrideAttachmentBudgetMb * BYTES_PER_MB)
        ),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('error.cacheOverride'));
    } finally {
      setIsSavingOverride(false);
    }
  };

  const resetCacheOverride = async () => {
    if (!overrideUserId) return;
    setError(null);
    setIsSavingOverride(true);
    try {
      await mailAdmin.resetCacheUserOverride(overrideUserId);
      setOverrideBodyBudgetMb(selectedOverrideDefaultBodyMb);
      setOverrideAttachmentBudgetMb(selectedOverrideDefaultAttachmentMb);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('error.cacheOverride'));
    } finally {
      setIsSavingOverride(false);
    }
  };

  const repairSearchIndex = async (userConfirmed: boolean) => {
    setError(null);
    setSearchRepairResult(null);
    setIsRepairingSearch(true);
    try {
      const result = await searchMaintenance.reindex({
        confirmed: userConfirmed,
        limit: 100,
      });
      if (result) {
        setSearchRepairResult(
          t('repair.result', {
            targets: formatCount(result.processedTargets),
            pages: formatCount(result.indexedPages),
            failures: formatCount(result.failedTargets),
          })
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('error.searchRepair'));
    } finally {
      setIsRepairingSearch(false);
    }
  };

  const rebuildSearchIndex = async () => {
    setError(null);
    setSearchIndexRebuildResult(null);
    setIsRebuildingSearchIndex(true);
    try {
      const result = await mailAdmin.rebuildSearchIndex(500);
      if (result) {
        setSearchIndexRebuildResult(
          result.jobId
            ? t('search.rebuildResult.backfill', { status: result.status })
            : t('search.rebuildResult.messages', {
                queued: formatCount(result.queuedMessages),
                skipped: formatCount(result.skippedMessages),
              })
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('error.searchRebuild'));
    } finally {
      setIsRebuildingSearchIndex(false);
    }
  };

  const repairablePdfAttachments = diagnostics?.pdfAttachmentsRepairable ?? 0;
  const lastSyncedAt = latestMailSyncAt(accounts);
  const searchOverview = formatSearchOverview(
    i18n,
    diagnostics,
    usage,
    searchIndexStatus
  );
  const searchBudgetProjection = formatSearchBudgetProjection(
    i18n,
    searchIndexStatus
  );
  const selectedPolicyPreset = getPolicyPresetOption(
    presetCatalog,
    policyPreset
  );
  const selectedPolicyLabel = presetLabel(i18n, policyPreset);
  const isCustomPolicy = policyPreset === MailPolicyPresetValue.custom;
  const presetEffectDetail = formatPresetEffect(
    i18n,
    selectedPolicyPreset,
    {
      searchIndexWindowDays,
      searchIndexMaxMessagesPerUser,
      bodyCacheTtlDays,
      attachmentCacheTtlDays,
      bodyCacheBudgetMb,
      attachmentCacheBudgetMb,
    },
    policyFallbacks
  );
  const providerCoverageNotice = formatProviderCoverageNotice(
    i18n,
    selectedPolicyPreset?.searchIndexWindowDays ?? searchIndexWindowDays,
    policyFallbacks.searchIndexWindowDays,
    uiBounds.searchIndexWindowDays.max
  );

  return (
    <>
      {header}
      <SettingsTabbedContent>
        <SettingsContentSurface>
          {mailAdmin.error ? (
            !backendAvailable || isBackendUnavailableError(mailAdmin.error) ? (
              <SettingsEntityMessage>{offlineLabel}</SettingsEntityMessage>
            ) : (
              <SettingsErrorBanner
                message={mailAdmin.error.message}
                onRetry={() => void mailAdmin.refresh()}
                retryLabel={t('actions.retry')}
              />
            )
          ) : null}

          {!mailAdmin.error ? (
            <>
              <SettingWrapper title={t('section.quota')}>
                <div className={styles.noticeBlock}>
                  <SettingsNotice
                    title={workspaceMailText(i18n, 'quotaNotice.title')}
                  >
                    {t('quotaNotice.body', {
                      providerCoverage: providerCoverageNotice,
                    })}
                  </SettingsNotice>
                </div>
                <div className={styles.presetGrid}>
                  {presetCatalog.map(option => (
                    <button
                      key={option.preset}
                      type="button"
                      className={styles.presetButton}
                      data-active={policyPreset === option.preset}
                      disabled={!policy}
                      onClick={() => setPolicyPreset(option.preset)}
                    >
                      <span className={styles.presetTitle}>
                        {presetLabel(i18n, option.preset)}
                      </span>
                      <span className={styles.presetMeta}>
                        {presetMeta(i18n, option.preset)}
                      </span>
                    </button>
                  ))}
                </div>
                <SettingRow
                  name={t('quota.activePreset.name')}
                  desc={presetEffectDetail}
                >
                  <span className={styles.settingValue}>
                    {selectedPolicyLabel}
                  </span>
                </SettingRow>
                <SettingRow
                  name={t('quota.providerBoundary.name')}
                  desc={t('quota.providerBoundary.desc')}
                >
                  <span className={styles.settingValue}>
                    {workspaceMailText(i18n, 'maxDays', {
                      days: formatCount(uiBounds.searchIndexWindowDays.max),
                    })}
                  </span>
                </SettingRow>
                <div className={styles.settingActions}>
                  <Button
                    variant="primary"
                    disabled={isSaving || !policy}
                    onClick={() => void savePolicy()}
                  >
                    {t('actions.savePreset')}
                  </Button>
                </div>
              </SettingWrapper>

              <SettingWrapper title={t('section.status')}>
                <SettingRow
                  name={t('status.accounts.name')}
                  desc={
                    accounts.length === 0
                      ? t('empty.noAccounts')
                      : t('status.accounts.detail', {
                          users: formatCount(mailUserTargets.length),
                          accounts: formatCount(accounts.length),
                        })
                  }
                >
                  <span className={styles.settingValue}>
                    {formatCount(accounts.length)}
                  </span>
                </SettingRow>
                <SettingRow
                  name={t('status.messages.name')}
                  desc={t('status.messages.detail', {
                    attachments: formatCount(usage?.attachmentCount),
                  })}
                >
                  <span className={styles.settingValue}>
                    {formatCount(usage?.messageCount)}
                  </span>
                </SettingRow>
                <SettingRow
                  name={t('status.lastSync.name')}
                  desc={t('status.lastSync.detail', {
                    synced: formatCount(countSyncedAccounts(accounts)),
                    total: formatCount(accounts.length),
                  })}
                >
                  <span className={styles.settingValue}>
                    {lastSyncedAt
                      ? formatMailDate(lastSyncedAt)
                      : t('sync.none')}
                  </span>
                </SettingRow>
                <SettingRow
                  name={t('searchIndex.name')}
                  desc={searchOverview.detail}
                >
                  <span className={styles.settingValue}>
                    {searchOverview.status}
                  </span>
                </SettingRow>
              </SettingWrapper>

              <SettingWrapper title={t('section.search')}>
                <div className={styles.noticeBlock}>
                  <SettingsNotice
                    title={workspaceMailText(i18n, 'searchNotice.title')}
                  >
                    {t('searchNotice.body')}
                  </SettingsNotice>
                </div>
                {isCustomPolicy ? (
                  <SettingRow
                    name={t('searchIndex.name')}
                    desc={t('searchIndex.desc')}
                    className={styles.policyRow}
                  >
                    <SettingsSelect
                      value={searchIndexMode}
                      options={searchIndexSelectOptions}
                      onChange={value => {
                        setPolicyPreset(MailPolicyPresetValue.custom);
                        setSearchIndexMode(value);
                      }}
                      disabled={!policy}
                    />
                  </SettingRow>
                ) : (
                  <SettingRow
                    name={t('search.rules.name')}
                    desc={presetEffectDetail}
                  >
                    <span className={styles.settingValue}>
                      {selectedPolicyLabel}
                    </span>
                  </SettingRow>
                )}
                <SettingRow
                  name={t('search.coverage.name')}
                  desc={formatSearchCoverageDetail(i18n, searchIndexStatus)}
                >
                  <span className={styles.settingValue}>
                    {formatSearchAdminStatus(i18n, searchIndexStatus?.status)}
                  </span>
                </SettingRow>
                <SettingRow
                  name={t('search.reach.name')}
                  desc={searchBudgetProjection.detail}
                >
                  <span className={styles.settingValue}>
                    {searchBudgetProjection.value}
                  </span>
                </SettingRow>
                <SettingRow
                  name={t('search.backfill.name')}
                  desc={formatSearchBackfillDetail(i18n, searchIndexStatus)}
                >
                  <span className={styles.settingValue}>
                    {formatSearchBackfillStatus(
                      i18n,
                      searchIndexStatus?.backfillStatus
                    )}
                  </span>
                </SettingRow>
                <SettingRow
                  name={t('search.notices.name')}
                  desc={formatSearchDegradedReasons(i18n, searchIndexStatus)}
                >
                  <span className={styles.settingValue}>
                    {formatCount(searchIndexStatus?.degradedReasons.length)}
                  </span>
                </SettingRow>
                {isCustomPolicy ? (
                  <>
                    <SettingRow
                      name={t('search.window.name')}
                      desc={t('search.window.desc')}
                      className={styles.policyRow}
                    >
                      <RangeNumberControl
                        value={searchIndexWindowDays}
                        min={uiBounds.searchIndexWindowDays.min}
                        max={uiBounds.searchIndexWindowDays.max}
                        fallback={policyFallbacks.searchIndexWindowDays}
                        unit={t('unit.days')}
                        disabled={!policy}
                        onChange={value => {
                          setPolicyPreset(MailPolicyPresetValue.custom);
                          setSearchIndexWindowDays(value);
                        }}
                      />
                    </SettingRow>
                    <SettingRow
                      name={t('search.maxMessages.name')}
                      desc={t('search.maxMessages.desc')}
                      className={styles.policyRow}
                    >
                      <RangeNumberControl
                        value={searchIndexMaxMessagesPerUser}
                        min={uiBounds.searchIndexMaxMessagesPerUser.min}
                        max={uiBounds.searchIndexMaxMessagesPerUser.max}
                        fallback={policyFallbacks.searchIndexMaxMessagesPerUser}
                        unit={t('unit.mails')}
                        disabled={!policy}
                        onChange={value => {
                          setPolicyPreset(MailPolicyPresetValue.custom);
                          setSearchIndexMaxMessagesPerUser(value);
                        }}
                      />
                    </SettingRow>
                    <SettingRow
                      name={t('search.workspaceBudget.name')}
                      desc={t('search.workspaceBudget.desc')}
                      className={styles.policyRow}
                    >
                      <RangeNumberControl
                        value={searchIndexWorkspaceBudgetMb}
                        min={uiBounds.searchIndexWorkspaceBudgetMb.min}
                        max={uiBounds.searchIndexWorkspaceBudgetMb.max}
                        fallback={policyFallbacks.searchIndexWorkspaceBudgetMb}
                        unit="MB"
                        disabled={!policy}
                        onChange={value => {
                          setPolicyPreset(MailPolicyPresetValue.custom);
                          setSearchIndexWorkspaceBudgetMb(value);
                        }}
                      />
                    </SettingRow>
                    <SettingRow
                      name={t('search.userBudget.name')}
                      desc={t('search.userBudget.desc')}
                      className={styles.policyRow}
                    >
                      <RangeNumberControl
                        value={searchIndexUserBudgetMb}
                        min={uiBounds.searchIndexUserBudgetMb.min}
                        max={Math.max(
                          uiBounds.searchIndexUserBudgetMb.min,
                          Math.min(
                            uiBounds.searchIndexUserBudgetMb.max,
                            searchIndexWorkspaceBudgetMb === ''
                              ? policyFallbacks.searchIndexWorkspaceBudgetMb
                              : searchIndexWorkspaceBudgetMb
                          )
                        )}
                        fallback={policyFallbacks.searchIndexUserBudgetMb}
                        unit="MB"
                        disabled={!policy}
                        onChange={value => {
                          setPolicyPreset(MailPolicyPresetValue.custom);
                          setSearchIndexUserBudgetMb(value);
                        }}
                      />
                    </SettingRow>
                    <SettingRow
                      name={t('search.bodyPerMail.name')}
                      desc={t('search.bodyPerMail.desc')}
                      className={styles.policyRow}
                    >
                      <RangeNumberControl
                        value={searchIndexBodyKb}
                        min={uiBounds.searchIndexBodyKb.min}
                        max={uiBounds.searchIndexBodyKb.max}
                        fallback={policyFallbacks.searchIndexBodyKb}
                        unit="KB"
                        disabled={!policy}
                        onChange={value => {
                          setPolicyPreset(MailPolicyPresetValue.custom);
                          setSearchIndexBodyKb(value);
                        }}
                      />
                    </SettingRow>
                  </>
                ) : null}
                <div className={styles.settingActions}>
                  <Button
                    variant="secondary"
                    disabled={isRebuildingSearchIndex || !policy}
                    onClick={() => void rebuildSearchIndex()}
                  >
                    {isRebuildingSearchIndex
                      ? t('actions.reindexQueued')
                      : t('actions.reindex')}
                  </Button>
                  {isCustomPolicy ? (
                    <Button
                      variant="primary"
                      disabled={isSaving || !policy}
                      onClick={() => void savePolicy()}
                    >
                      {t('actions.saveSearchRules')}
                    </Button>
                  ) : null}
                </div>
                {searchIndexRebuildResult ? (
                  <SettingsEntityMessage>
                    {searchIndexRebuildResult}
                  </SettingsEntityMessage>
                ) : null}
              </SettingWrapper>

              <SettingWrapper title={t('section.accounts')}>
                <SettingsEntityList>
                  {mailUserTargets.length === 0 ? (
                    <SettingsEntityMessage>
                      {t('empty.noAccounts')}
                    </SettingsEntityMessage>
                  ) : null}
                  {mailUserTargets.map(target => {
                    const quotaUser = quotaUserById.get(target.userId);
                    const worstFactor = quotaUser
                      ? findQuotaFactor(
                          quotaUser.factors,
                          quotaUser.worstFactorKind
                        )
                      : null;
                    return (
                      <SettingsExpandableItem
                        key={target.userId}
                        expanded={expandedAccountUserId === target.userId}
                        onToggle={() =>
                          setExpandedAccountUserId(current =>
                            current === target.userId ? null : target.userId
                          )
                        }
                        leading={<SettingsEntityAvatar name={target.label} />}
                        title={target.label}
                        badges={
                          <>
                            <SettingsBadge variant="soft">
                              {formatMailAccountCount(
                                i18n,
                                target.accounts.length
                              )}
                            </SettingsBadge>
                            {worstFactor ? (
                              <SettingsBadge
                                variant={quotaStatusBadgeVariant(
                                  worstFactor.status
                                )}
                              >
                                {worstFactor.label}
                              </SettingsBadge>
                            ) : null}
                          </>
                        }
                        aside={
                          worstFactor ? (
                            <QuotaProgress
                              percent={worstFactor.percent}
                              status={worstFactor.status}
                            />
                          ) : null
                        }
                        meta={
                          <>
                            <SettingsMetaItem>
                              {target.userEmail || t('accounts.noUserEmail')}
                            </SettingsMetaItem>
                            <SettingsMetaItem>
                              {target.latestSyncedAt
                                ? t('sync.last', {
                                    date: formatMailDate(target.latestSyncedAt),
                                  })
                                : t('sync.none')}
                            </SettingsMetaItem>
                          </>
                        }
                      >
                        <div className={styles.expandedPanel}>
                          {quotaUser ? (
                            <QuotaFactorList
                              factors={quotaUser.factors}
                              i18n={i18n}
                            />
                          ) : null}
                          <div className={styles.mailAccountList}>
                            {target.accounts.map(account => (
                              <div
                                className={styles.mailAccountRow}
                                key={account.id}
                              >
                                <div className={styles.mailAccountMain}>
                                  <span className={styles.mailAccountTitle}>
                                    {account.emailAddress}
                                  </span>
                                  <span className={styles.mailAccountMeta}>
                                    <SettingsMetaItem>
                                      {formatMailProvider(account.providerKind)}
                                    </SettingsMetaItem>
                                    <SettingsMetaItem>
                                      {account.lastSyncedAt
                                        ? t('sync.account', {
                                            date: formatMailDate(
                                              account.lastSyncedAt
                                            ),
                                          })
                                        : t('sync.never')}
                                    </SettingsMetaItem>
                                  </span>
                                </div>
                                {renderAccountQuotaProgress(
                                  quotaUser,
                                  account.id
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </SettingsExpandableItem>
                    );
                  })}
                </SettingsEntityList>
              </SettingWrapper>

              <SettingWrapper title={t('section.workspaceRules')}>
                <div className={styles.noticeBlock}>
                  <SettingsNotice title={t('retentionNotice.title')}>
                    {t('retentionNotice.body')}
                  </SettingsNotice>
                </div>
                {isCustomPolicy ? (
                  <>
                    <SettingRow
                      name={t('retention.name')}
                      desc={t('retention.desc')}
                      className={styles.policyRow}
                    >
                      <SettingsSelect
                        value={retentionMode}
                        options={retentionSelectOptions}
                        onChange={value => {
                          setPolicyPreset(MailPolicyPresetValue.custom);
                          setRetentionMode(value);
                        }}
                        disabled={!policy}
                      />
                    </SettingRow>
                    {retentionMode === MailRetentionModeValue.days ? (
                      <SettingRow
                        name={t('retention.days.name')}
                        desc={t('retention.days.desc')}
                        className={styles.policyRow}
                      >
                        <RangeNumberControl
                          value={retentionDays}
                          min={uiBounds.retentionDays.min}
                          max={uiBounds.retentionDays.max}
                          fallback={policyFallbacks.retentionDays}
                          unit={t('unit.days')}
                          disabled={!policy}
                          onChange={value => {
                            setPolicyPreset(MailPolicyPresetValue.custom);
                            setRetentionDays(value);
                          }}
                        />
                      </SettingRow>
                    ) : null}
                    <SettingRow
                      name={t('cache.body.name')}
                      desc={t('cache.body.desc')}
                      className={styles.policyRow}
                    >
                      <RangeNumberControl
                        value={bodyCacheTtlDays}
                        min={uiBounds.bodyCacheTtlDays.min}
                        max={uiBounds.bodyCacheTtlDays.max}
                        fallback={policyFallbacks.bodyCacheTtlDays}
                        unit={t('unit.days')}
                        disabled={!policy}
                        onChange={value => {
                          setPolicyPreset(MailPolicyPresetValue.custom);
                          setBodyCacheTtlDays(value);
                        }}
                      />
                    </SettingRow>
                    <SettingRow
                      name={t('cache.attachments.name')}
                      desc={t('cache.attachments.desc')}
                      className={styles.policyRow}
                    >
                      <RangeNumberControl
                        value={attachmentCacheTtlDays}
                        min={uiBounds.attachmentCacheTtlDays.min}
                        max={uiBounds.attachmentCacheTtlDays.max}
                        fallback={policyFallbacks.attachmentCacheTtlDays}
                        unit={t('unit.days')}
                        disabled={!policy}
                        onChange={value => {
                          setPolicyPreset(MailPolicyPresetValue.custom);
                          setAttachmentCacheTtlDays(value);
                        }}
                      />
                    </SettingRow>
                    <SettingRow
                      name={t('cache.bodyBudget.name')}
                      desc={t('cache.bodyBudget.desc')}
                      className={styles.policyRow}
                    >
                      <RangeNumberControl
                        value={bodyCacheBudgetMb}
                        min={uiBounds.bodyCacheBudgetMb.min}
                        max={Math.min(
                          cacheBudgetMaxMb,
                          uiBounds.bodyCacheBudgetMb.max
                        )}
                        fallback={policyFallbacks.bodyCacheBudgetMb}
                        unit="MB"
                        disabled={!policy}
                        onChange={value => {
                          setPolicyPreset(MailPolicyPresetValue.custom);
                          setBodyCacheBudgetMb(value);
                        }}
                      />
                    </SettingRow>
                    <SettingRow
                      name={t('cache.attachmentBudget.name')}
                      desc={t('cache.attachmentBudget.desc')}
                      className={styles.policyRow}
                    >
                      <RangeNumberControl
                        value={attachmentCacheBudgetMb}
                        min={uiBounds.attachmentCacheBudgetMb.min}
                        max={Math.min(
                          cacheBudgetMaxMb,
                          uiBounds.attachmentCacheBudgetMb.max
                        )}
                        fallback={policyFallbacks.attachmentCacheBudgetMb}
                        unit="MB"
                        disabled={!policy}
                        onChange={value => {
                          setPolicyPreset(MailPolicyPresetValue.custom);
                          setAttachmentCacheBudgetMb(value);
                        }}
                      />
                    </SettingRow>
                    <div className={styles.settingActions}>
                      <Button
                        variant="primary"
                        disabled={isSaving || !policy}
                        onClick={() => void savePolicy()}
                      >
                        {t('actions.saveRules')}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <SettingRow
                      name={t('retention.name')}
                      desc={t('retention.presetDesc')}
                    >
                      <span className={styles.settingValue}>
                        {t('retention.streamlainOnly')}
                      </span>
                    </SettingRow>
                    <SettingRow
                      name={t('cache.rules.name')}
                      desc={presetEffectDetail}
                    >
                      <span className={styles.settingValue}>
                        {selectedPolicyLabel}
                      </span>
                    </SettingRow>
                  </>
                )}
              </SettingWrapper>

              <SettingWrapper title={t('section.userOverrides')}>
                <div className={styles.listControlsBlock}>
                  <SettingsListControls
                    searchValue={overrideSearchQuery}
                    onSearchChange={setOverrideSearchQuery}
                    searchPlaceholder={t('userOverrides.searchPlaceholder')}
                  />
                </div>
                <SettingsEntityList>
                  {mailUserTargets.length === 0 ? (
                    <SettingsEntityMessage>
                      {t('empty.noAccounts')}
                    </SettingsEntityMessage>
                  ) : null}
                  {mailUserTargets.length > 0 &&
                  filteredCacheOverrideTargets.length === 0 ? (
                    <SettingsEntityMessage>
                      {t('empty.noUsersFound')}
                    </SettingsEntityMessage>
                  ) : null}
                  {filteredCacheOverrideTargets.map(target => {
                    const override = cacheUserOverrides.find(
                      row => row.userId === target.userId
                    );
                    const isExpanded = overrideUserId === target.userId;
                    const effectiveBodyBudgetMb = override
                      ? bytesToMb(
                          override.bodyCacheBudgetBytes,
                          policyFallbacks.bodyCacheBudgetMb
                        )
                      : getDefaultUserCacheBudgetMb(
                          target,
                          policyBodyBudgetMb,
                          uiBounds.bodyCacheBudgetMb.min
                        );
                    const effectiveAttachmentBudgetMb = override
                      ? bytesToMb(
                          override.attachmentCacheBudgetBytes,
                          policyFallbacks.attachmentCacheBudgetMb
                        )
                      : getDefaultUserCacheBudgetMb(
                          target,
                          policyAttachmentBudgetMb,
                          uiBounds.attachmentCacheBudgetMb.min
                        );
                    return (
                      <SettingsExpandableItem
                        key={target.userId}
                        expanded={isExpanded}
                        onToggle={() => {
                          const nextUserId = isExpanded ? '' : target.userId;
                          setOverrideUserId(nextUserId);
                          setOverrideBodyBudgetMb(
                            override
                              ? bytesToMb(
                                  override.bodyCacheBudgetBytes,
                                  policyFallbacks.bodyCacheBudgetMb
                                )
                              : getDefaultUserCacheBudgetMb(
                                  target,
                                  policyBodyBudgetMb,
                                  uiBounds.bodyCacheBudgetMb.min
                                )
                          );
                          setOverrideAttachmentBudgetMb(
                            override
                              ? bytesToMb(
                                  override.attachmentCacheBudgetBytes,
                                  policyFallbacks.attachmentCacheBudgetMb
                                )
                              : getDefaultUserCacheBudgetMb(
                                  target,
                                  policyAttachmentBudgetMb,
                                  uiBounds.attachmentCacheBudgetMb.min
                                )
                          );
                        }}
                        leading={<SettingsEntityAvatar name={target.label} />}
                        title={target.label}
                        badges={
                          <>
                            <SettingsBadge
                              variant={override ? 'active' : 'soft'}
                            >
                              {override
                                ? t('userOverrides.badge.override')
                                : t('userOverrides.badge.default')}
                            </SettingsBadge>
                            <SettingsBadge variant="soft">
                              {t('userOverrides.badge.body', {
                                budget: formatCount(effectiveBodyBudgetMb),
                              })}
                            </SettingsBadge>
                            <SettingsBadge variant="soft">
                              {t('userOverrides.badge.attachments', {
                                budget: formatCount(
                                  effectiveAttachmentBudgetMb
                                ),
                              })}
                            </SettingsBadge>
                          </>
                        }
                        meta={
                          <SettingsMetaItem>
                            {target.userEmail}
                          </SettingsMetaItem>
                        }
                      >
                        <div className={styles.expandedPanel}>
                          <div className={styles.expandedControlRow}>
                            <div className={styles.rowText}>
                              <span className={styles.rowLabel}>
                                {t('userOverrides.bodyBudget.name')}
                              </span>
                              <span className={styles.rowDesc}>
                                {t('userOverrides.bodyBudget.desc')}
                              </span>
                            </div>
                            <RangeNumberControl
                              value={overrideBodyBudgetMb}
                              min={uiBounds.bodyCacheBudgetMb.min}
                              max={target.budgetMaxMb}
                              fallback={getDefaultUserCacheBudgetMb(
                                target,
                                policyBodyBudgetMb,
                                uiBounds.bodyCacheBudgetMb.min
                              )}
                              unit="MB"
                              onChange={setOverrideBodyBudgetMb}
                            />
                          </div>
                          <div className={styles.expandedControlRow}>
                            <div className={styles.rowText}>
                              <span className={styles.rowLabel}>
                                {t('userOverrides.attachmentBudget.name')}
                              </span>
                              <span className={styles.rowDesc}>
                                {t('userOverrides.attachmentBudget.desc')}
                              </span>
                            </div>
                            <RangeNumberControl
                              value={overrideAttachmentBudgetMb}
                              min={uiBounds.attachmentCacheBudgetMb.min}
                              max={target.budgetMaxMb}
                              fallback={getDefaultUserCacheBudgetMb(
                                target,
                                policyAttachmentBudgetMb,
                                uiBounds.attachmentCacheBudgetMb.min
                              )}
                              unit="MB"
                              onChange={setOverrideAttachmentBudgetMb}
                            />
                          </div>
                          <div className={styles.expandedActions}>
                            <Button
                              variant="primary"
                              disabled={!overrideUserId || isSavingOverride}
                              onClick={() => void saveCacheOverride()}
                            >
                              {t('actions.saveOverride')}
                            </Button>
                            <Button
                              variant="secondary"
                              disabled={
                                !selectedOverride ||
                                !overrideUserId ||
                                isSavingOverride
                              }
                              onClick={() => void resetCacheOverride()}
                            >
                              {t('actions.deleteOverride')}
                            </Button>
                          </div>
                        </div>
                      </SettingsExpandableItem>
                    );
                  })}
                </SettingsEntityList>
              </SettingWrapper>

              <SettingWrapper title={t('section.pdfRepair')}>
                {searchMaintenance.error ? (
                  !backendAvailable ||
                  isBackendUnavailableError(searchMaintenance.error) ? (
                    <SettingsEntityMessage>
                      {offlineLabel}
                    </SettingsEntityMessage>
                  ) : (
                    <SettingsErrorBanner
                      message={searchMaintenance.error.message}
                      onRetry={() => void searchMaintenance.refresh()}
                      retryLabel={t('actions.retry')}
                    />
                  )
                ) : null}
                <SettingRow
                  name={t('pdfRepair.status.name')}
                  desc={t('pdfRepair.status.desc')}
                >
                  <span className={styles.settingValue}>
                    {formatSearchStatus(
                      i18n,
                      diagnostics?.scopeStatus ?? 'unknown'
                    )}
                  </span>
                </SettingRow>
                <SettingRow
                  name={t('status.messages.name')}
                  desc={t('pdfRepair.messages.desc')}
                >
                  <span className={styles.settingValue}>
                    {formatCount(diagnostics?.totalMessages)}
                  </span>
                </SettingRow>
                <SettingRow
                  name={t('pdfRepair.pdfs.name')}
                  desc={formatPdfIndexSummary(i18n, diagnostics)}
                >
                  <span className={styles.settingValue}>
                    {formatCount(diagnostics?.totalPdfAttachments)}
                  </span>
                </SettingRow>
                <SettingRow
                  name={t('pdfRepair.attention.name')}
                  desc={formatSearchAttentionSummary(i18n, diagnostics)}
                >
                  <span className={styles.settingValue}>
                    {formatCount(countSearchAttentionDiagnostics(diagnostics))}
                  </span>
                </SettingRow>
                <SettingRow
                  name={t('pdfRepair.missingPermission.name')}
                  desc={t('pdfRepair.missingPermission.desc')}
                >
                  <span className={styles.settingValue}>
                    {formatCount(diagnostics?.missingPermissionTargets)}
                  </span>
                </SettingRow>
                <div className={styles.settingActions}>
                  <Button
                    variant="secondary"
                    disabled={searchMaintenance.isLoading}
                    onClick={() => void searchMaintenance.refresh()}
                  >
                    {t('actions.refresh')}
                  </Button>
                  <SettingsConfirmButton
                    variant="secondary"
                    label={
                      isRepairingSearch
                        ? t('actions.repairing')
                        : t('actions.repairPdf')
                    }
                    confirmLabel={t('actions.startRepair')}
                    cancelLabel={t('actions.cancel')}
                    loadingLabel={t('actions.repairing')}
                    loading={isRepairingSearch}
                    disabled={
                      isRepairingSearch ||
                      searchMaintenance.isLoading ||
                      repairablePdfAttachments === 0
                    }
                    onConfirm={() => void repairSearchIndex(true)}
                  />
                </div>
                {searchRepairResult ? (
                  <SettingsEntityMessage>
                    {searchRepairResult}
                  </SettingsEntityMessage>
                ) : null}
                <SettingsEntityList>
                  {(diagnostics?.messages ?? []).length === 0 ? (
                    <SettingsEntityMessage>
                      {t('empty.noIndexDetails')}
                    </SettingsEntityMessage>
                  ) : null}
                  {(diagnostics?.messages ?? []).map(message => (
                    <SettingsEntityMessage key={message.messageId}>
                      <span className={styles.accountRow}>
                        <span className={styles.entityMain}>
                          {formatDiagnosticMessageLabel(
                            i18n,
                            message.messageId
                          )}
                        </span>
                        <span className={styles.entityMeta}>
                          <SettingsBadge
                            variant={searchStatusVariant(message.ocrStatus)}
                          >
                            {formatSearchStatus(i18n, message.ocrStatus)}
                          </SettingsBadge>
                          <SettingsMetaItem>
                            {t('pdfRepair.messageMeta', {
                              pdfs: formatCount(message.pdfAttachmentCount),
                              pages: formatCount(message.indexedPdfPageCount),
                            })}
                          </SettingsMetaItem>
                        </span>
                      </span>
                    </SettingsEntityMessage>
                  ))}
                </SettingsEntityList>
              </SettingWrapper>
            </>
          ) : null}

          {error ? <div className={styles.errorText}>{error}</div> : null}
        </SettingsContentSurface>
      </SettingsTabbedContent>
    </>
  );
};

function clampNumber(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(Math.max(Math.round(value), min), max);
}

type NumberBounds = { min: number; max: number };

type PolicyUiBounds = {
  retentionDays: NumberBounds;
  searchIndexWindowDays: NumberBounds;
  searchIndexMaxMessagesPerUser: NumberBounds;
  searchIndexWorkspaceBudgetMb: NumberBounds;
  searchIndexUserBudgetMb: NumberBounds;
  searchIndexBodyKb: NumberBounds;
  bodyCacheTtlDays: NumberBounds;
  attachmentCacheTtlDays: NumberBounds;
  bodyCacheBudgetMb: NumberBounds;
  attachmentCacheBudgetMb: NumberBounds;
  cacheBudgetMaxMb: number;
};

type PolicyFallbacks = {
  retentionDays: number;
  searchIndexWindowDays: number;
  searchIndexMaxMessagesPerUser: number;
  searchIndexWorkspaceBudgetMb: number;
  searchIndexUserBudgetMb: number;
  searchIndexBodyKb: number;
  bodyCacheTtlDays: number;
  attachmentCacheTtlDays: number;
  bodyCacheBudgetMb: number;
  attachmentCacheBudgetMb: number;
};

function emptyBounds(): NumberBounds {
  return { min: 0, max: 0 };
}

function numberBounds(bound?: NumberBounds | null): NumberBounds {
  return bound ? { min: bound.min, max: bound.max } : emptyBounds();
}

function byteBoundsToMb(
  bound?: { minBytes: string; maxBytes: string } | null
): NumberBounds {
  return bound
    ? {
        min: bytesToMb(bound.minBytes),
        max: bytesToMb(bound.maxBytes),
      }
    : emptyBounds();
}

function byteBoundsToKb(bound?: NumberBounds | null): NumberBounds {
  return bound
    ? {
        min: Math.round(bound.min / BYTES_PER_KB),
        max: Math.round(bound.max / BYTES_PER_KB),
      }
    : emptyBounds();
}

function toPolicyUiBounds(
  customBounds: MailPolicyCustomBounds | undefined,
  cacheBudgetMaxBytes: string | undefined
): PolicyUiBounds {
  const bodyCacheBudgetMb = byteBoundsToMb(customBounds?.bodyCacheBudgetBytes);
  const attachmentCacheBudgetMb = byteBoundsToMb(
    customBounds?.attachmentCacheBudgetBytes
  );
  const cacheBudgetMaxMb = bytesToMb(
    cacheBudgetMaxBytes,
    bodyCacheBudgetMb.max
  );
  return {
    retentionDays: numberBounds(customBounds?.retentionDays),
    searchIndexWindowDays: numberBounds(customBounds?.searchIndexWindowDays),
    searchIndexMaxMessagesPerUser: numberBounds(
      customBounds?.searchIndexMaxMessagesPerUser
    ),
    searchIndexWorkspaceBudgetMb: byteBoundsToMb(
      customBounds?.searchIndexMaxWorkspaceBytes
    ),
    searchIndexUserBudgetMb: byteBoundsToMb(
      customBounds?.searchIndexMaxUserBytes
    ),
    searchIndexBodyKb: byteBoundsToKb(customBounds?.searchIndexBodyMaxBytes),
    bodyCacheTtlDays: numberBounds(customBounds?.bodyCacheTtlDays),
    attachmentCacheTtlDays: numberBounds(customBounds?.attachmentCacheTtlDays),
    bodyCacheBudgetMb,
    attachmentCacheBudgetMb,
    cacheBudgetMaxMb,
  };
}

function toPolicyFallbacks(
  policy: MailAdminOverview['policy'] | undefined,
  bounds: PolicyUiBounds
): PolicyFallbacks {
  return {
    retentionDays: policy?.retentionDays ?? bounds.retentionDays.min,
    searchIndexWindowDays:
      policy?.searchIndexWindowDays ?? bounds.searchIndexWindowDays.min,
    searchIndexMaxMessagesPerUser:
      policy?.searchIndexMaxMessagesPerUser ??
      bounds.searchIndexMaxMessagesPerUser.min,
    searchIndexWorkspaceBudgetMb: bytesToMb(
      policy?.searchIndexMaxWorkspaceBytes,
      bounds.searchIndexWorkspaceBudgetMb.min
    ),
    searchIndexUserBudgetMb: bytesToMb(
      policy?.searchIndexMaxUserBytes,
      bounds.searchIndexUserBudgetMb.min
    ),
    searchIndexBodyKb: Math.round(
      (policy?.searchIndexBodyMaxBytes ??
        bounds.searchIndexBodyKb.min * BYTES_PER_KB) / BYTES_PER_KB
    ),
    bodyCacheTtlDays: policy?.bodyCacheTtlDays ?? bounds.bodyCacheTtlDays.min,
    attachmentCacheTtlDays:
      policy?.attachmentCacheTtlDays ?? bounds.attachmentCacheTtlDays.min,
    bodyCacheBudgetMb: bytesToMb(
      policy?.bodyCacheBudgetBytes,
      bounds.bodyCacheBudgetMb.min
    ),
    attachmentCacheBudgetMb: bytesToMb(
      policy?.attachmentCacheBudgetBytes,
      bounds.attachmentCacheBudgetMb.min
    ),
  };
}

function resolveNumber(
  presetValue: number | null | undefined,
  currentValue: number | '',
  fallback: number
): number {
  if (presetValue !== null && presetValue !== undefined) return presetValue;
  return currentValue === '' ? fallback : currentValue;
}

function formatSearchStatus(
  i18n: ReturnType<typeof useI18n>,
  status: MailSearchIndexStatus | 'unknown'
): string {
  switch (status) {
    case 'indexed':
      return workspaceMailText(i18n, 'searchStatus.indexed');
    case 'missing':
      return workspaceMailText(i18n, 'searchStatus.missing');
    case 'stale':
      return workspaceMailText(i18n, 'searchStatus.stale');
    case 'failed':
      return workspaceMailText(i18n, 'searchStatus.failed');
    case 'missing_permission':
      return workspaceMailText(i18n, 'searchStatus.missingPermission');
    case 'none':
      return workspaceMailText(i18n, 'searchStatus.none');
    case 'unknown':
    default:
      return workspaceMailText(i18n, 'searchStatus.unknown');
  }
}

function formatCount(value: number | null | undefined): string {
  return COUNT_FORMATTER.format(value ?? 0);
}

function formatMailAccountCount(
  i18n: ReturnType<typeof useI18n>,
  value: number
): string {
  return workspaceMailText(
    i18n,
    value === 1 ? 'mailAccountCount.one' : 'mailAccountCount.other',
    { count: formatCount(value) }
  );
}

function findQuotaFactor(
  factors: MailQuotaFactorOverview[],
  kind: MailQuotaUserOverview['worstFactorKind']
): MailQuotaFactorOverview | null {
  return factors.find(factor => factor.kind === kind) ?? factors[0] ?? null;
}

function quotaStatusBadgeVariant(status: string): 'soft' | 'active' | 'error' {
  if (status === 'ok') return 'active';
  if (status === 'danger' || status === 'exceeded') return 'error';
  return 'soft';
}

function renderAccountQuotaProgress(
  quotaUser: MailQuotaUserOverview | undefined,
  accountId: string
) {
  const account = quotaUser?.accounts.find(row => row.accountId === accountId);
  if (!account) return null;
  const worstFactor = findQuotaFactor(account.factors, account.worstFactorKind);
  if (!worstFactor) return null;
  return (
    <QuotaProgress percent={worstFactor.percent} status={worstFactor.status} />
  );
}

function formatQuotaFactorDetail(
  i18n: ReturnType<typeof useI18n>,
  factor: MailQuotaFactorOverview
): string {
  const detail = formatQuotaFactorDescription(i18n, factor);
  if (factor.usedBytes !== null && factor.limitBytes !== null) {
    return workspaceMailText(i18n, 'quotaFactor.bytes', {
      used: formatBytesFromString(factor.usedBytes),
      limit: formatBytesFromString(factor.limitBytes),
      detail,
    });
  }
  if (factor.usedCount !== null && factor.limitCount !== null) {
    return workspaceMailText(i18n, 'quotaFactor.count', {
      used: formatCount(factor.usedCount),
      limit: formatCount(factor.limitCount),
      detail,
    });
  }
  return detail;
}

function formatQuotaFactorLabel(
  i18n: ReturnType<typeof useI18n>,
  factor: MailQuotaFactorOverview
): string {
  switch (factor.kind) {
    case 'body_cache':
    case 'attachment_cache':
    case 'search_index':
    case 'indexed_messages':
    case 'search_window_coverage':
      return workspaceMailText(i18n, `quotaFactor.${factor.kind}.label`);
    default:
      return factor.label;
  }
}

function formatQuotaFactorDescription(
  i18n: ReturnType<typeof useI18n>,
  factor: MailQuotaFactorOverview
): string {
  switch (factor.kind) {
    case 'body_cache':
    case 'attachment_cache':
    case 'search_index':
    case 'indexed_messages':
      return workspaceMailText(i18n, `quotaFactor.${factor.kind}.detail`);
    case 'search_window_coverage': {
      const missingCount = Math.max(
        0,
        (factor.limitCount ?? 0) - (factor.usedCount ?? 0)
      );
      return workspaceMailText(
        i18n,
        missingCount > 0
          ? 'quotaFactor.search_window_coverage.missing'
          : 'quotaFactor.search_window_coverage.complete',
        { count: formatCount(missingCount) }
      );
    }
    default:
      return factor.detail;
  }
}

function getDefaultUserCacheBudgetMb(
  target: MailUserTarget,
  perMailboxBudgetMb: number,
  minMb: number
): number {
  return clampNumber(perMailboxBudgetMb, minMb, target.budgetMaxMb);
}

function countSyncedAccounts(accounts: MailAccountOverview[]): number {
  return accounts.filter(account => Boolean(account.lastSyncedAt)).length;
}

function latestMailSyncAt(accounts: MailAccountOverview[]): string | null {
  let latest: string | null = null;
  for (const account of accounts) {
    if (!account.lastSyncedAt) continue;
    if (
      !latest ||
      new Date(account.lastSyncedAt).getTime() > new Date(latest).getTime()
    ) {
      latest = account.lastSyncedAt;
    }
  }
  return latest;
}

function formatSearchAdminStatus(
  i18n: ReturnType<typeof useI18n>,
  status: string | undefined
): string {
  switch (status) {
    case 'healthy':
      return workspaceMailText(i18n, 'searchAdminStatus.healthy');
    case 'indexing':
      return workspaceMailText(i18n, 'searchAdminStatus.indexing');
    case 'pending':
      return workspaceMailText(i18n, 'searchAdminStatus.pending');
    case 'degraded':
      return workspaceMailText(i18n, 'searchAdminStatus.degraded');
    case 'disabled':
      return workspaceMailText(i18n, 'searchAdminStatus.disabled');
    default:
      return workspaceMailText(i18n, 'searchAdminStatus.unknown');
  }
}

function formatSearchBackfillStatus(
  i18n: ReturnType<typeof useI18n>,
  status: string | undefined
): string {
  switch (status) {
    case 'queued':
      return workspaceMailText(i18n, 'backfillStatus.queued');
    case 'running':
      return workspaceMailText(i18n, 'backfillStatus.running');
    case 'paused':
      return workspaceMailText(i18n, 'backfillStatus.paused');
    case 'completed':
      return workspaceMailText(i18n, 'backfillStatus.completed');
    case 'failed':
      return workspaceMailText(i18n, 'backfillStatus.failed');
    case 'cancelled':
      return workspaceMailText(i18n, 'backfillStatus.cancelled');
    case 'not_found':
      return workspaceMailText(i18n, 'backfillStatus.notFound');
    default:
      return workspaceMailText(i18n, 'backfillStatus.unknown');
  }
}

function formatSearchCoverageDetail(
  i18n: ReturnType<typeof useI18n>,
  status: MailSearchAdminStatus | undefined
): string {
  if (!status) return workspaceMailText(i18n, 'search.coverage.empty');
  return workspaceMailText(i18n, 'search.coverage.detail', {
    eligible: formatCount(status.eligibleMessageCount),
    open: formatCount(status.unindexedMessageCount),
    percent: COUNT_FORMATTER.format(status.coveragePercent),
  });
}

function formatSearchBudgetProjection(
  i18n: ReturnType<typeof useI18n>,
  status: MailSearchAdminStatus | undefined
): {
  value: string;
  detail: string;
} {
  if (!status) {
    return {
      value: workspaceMailText(i18n, 'search.budget.noData.value'),
      detail: workspaceMailText(i18n, 'search.budget.noData.detail'),
    };
  }
  if (status.status === 'disabled') {
    return {
      value: workspaceMailText(i18n, 'search.budget.paused.value'),
      detail: workspaceMailText(i18n, 'search.budget.paused.detail'),
    };
  }
  if (status.avgIndexedBytesPerMessage === '0') {
    return {
      value: workspaceMailText(i18n, 'search.budget.pending.value'),
      detail: workspaceMailText(i18n, 'search.budget.pending.detail'),
    };
  }
  return {
    value: workspaceMailText(i18n, 'search.budget.value', {
      messages: formatCount(status.projectedMessagesWithinWorkspaceBudget),
    }),
    detail: workspaceMailText(i18n, 'search.budget.detail', {
      bytes: formatBytesFromString(status.avgIndexedBytesPerMessage),
      days: formatCount(status.projectedDaysWithinWorkspaceBudget),
    }),
  };
}

function formatSearchBackfillDetail(
  i18n: ReturnType<typeof useI18n>,
  status: MailSearchAdminStatus | undefined
): string {
  if (!status) return workspaceMailText(i18n, 'backfillDetail.empty');
  if (status.backfillLastError) {
    return status.backfillLastError.replace('backpressure:', 'Backpressure: ');
  }
  if (status.backfillStatus === 'not_found') {
    return workspaceMailText(i18n, 'backfillDetail.notStarted');
  }
  if (status.backfillCompletedAt) {
    return workspaceMailText(i18n, 'backfillDetail.completed', {
      date: formatMailDate(status.backfillCompletedAt),
    });
  }
  if (status.backfillCursorDate) {
    return workspaceMailText(i18n, 'backfillDetail.cursor', {
      date: formatMailDate(status.backfillCursorDate),
      jobs: formatCount(status.backfillQueuedCount),
    });
  }
  return workspaceMailText(i18n, 'backfillDetail.progress', {
    scanned: formatCount(status.backfillScannedCount),
    jobs: formatCount(status.backfillQueuedCount),
  });
}

function formatSearchDegradedReasons(
  i18n: ReturnType<typeof useI18n>,
  status: MailSearchAdminStatus | undefined
): string {
  if (!status || status.degradedReasons.length === 0) {
    return workspaceMailText(i18n, 'search.notices.empty');
  }
  return status.degradedReasons
    .slice(0, 3)
    .map(reason => formatSearchDegradedReason(i18n, reason))
    .join(', ');
}

function formatSearchDegradedReason(
  i18n: ReturnType<typeof useI18n>,
  reason: string
): string {
  switch (reason) {
    case 'coverage_gap':
      return workspaceMailText(i18n, 'degradedReason.coverageGap');
    case 'pending_jobs':
      return workspaceMailText(i18n, 'degradedReason.pendingJobs');
    case 'failed_jobs':
      return workspaceMailText(i18n, 'degradedReason.failedJobs');
    case 'skipped_by_policy_or_quota':
      return workspaceMailText(i18n, 'degradedReason.skipped');
    case 'index_disabled':
      return workspaceMailText(i18n, 'degradedReason.indexDisabled');
    case 'workspace_index_budget_exhausted':
      return workspaceMailText(i18n, 'degradedReason.workspaceBudget');
    case 'backfill_failed':
      return workspaceMailText(i18n, 'degradedReason.backfillFailed');
    default:
      return reason.replace('backpressure:', 'Backpressure: ');
  }
}

function formatSearchOverview(
  i18n: ReturnType<typeof useI18n>,
  diagnostics: MailSearchDiagnostics,
  usage: MailAdminOverview['usage'] | undefined,
  status: MailSearchAdminStatus | undefined
): {
  status: string;
  detail: string;
} {
  if (status) {
    if (status.status === 'degraded') {
      return {
        status: workspaceMailText(i18n, 'overview.indexCheck'),
        detail: formatSearchDegradedReasons(i18n, status),
      };
    }
    return {
      status: formatSearchAdminStatus(i18n, status.status),
      detail: formatSearchCoverageDetail(i18n, status),
    };
  }

  if (usage) {
    if (usage.searchFailedMessageCount > 0) {
      return {
        status: workspaceMailText(i18n, 'overview.errorCheck'),
        detail: workspaceMailText(i18n, 'overview.indexErrors', {
          count: formatCount(usage.searchFailedMessageCount),
        }),
      };
    }
    const openCount =
      usage.searchPendingMessageCount + usage.searchSkippedMessageCount;
    if (openCount > 0) {
      return {
        status: workspaceMailText(i18n, 'overview.indexCheck'),
        detail: workspaceMailText(i18n, 'overview.indexOpen', {
          indexed: formatCount(usage.searchIndexedMessageCount),
          open: formatCount(openCount),
        }),
      };
    }
    return {
      status: workspaceMailText(i18n, 'searchStatus.indexed'),
      detail: workspaceMailText(i18n, 'overview.indexed', {
        mails: formatCount(usage.searchIndexedMessageCount),
        bytes: formatBytesFromString(usage.searchIndexBytes),
      }),
    };
  }

  if (!diagnostics) {
    return {
      status: workspaceMailText(i18n, 'searchStatus.unknown'),
      detail: workspaceMailText(i18n, 'overview.diagnosticsMissing'),
    };
  }

  if (diagnostics.missingPermissionTargets > 0) {
    return {
      status: workspaceMailText(i18n, 'overview.accessCheck'),
      detail: workspaceMailText(i18n, 'attention.missingPermission', {
        count: formatCount(diagnostics.missingPermissionTargets),
      }),
    };
  }

  if (diagnostics.pdfAttachmentsFailed > 0) {
    return {
      status: workspaceMailText(i18n, 'overview.errorCheck'),
      detail: workspaceMailText(i18n, 'overview.pdfErrors', {
        count: formatCount(diagnostics.pdfAttachmentsFailed),
      }),
    };
  }

  if (
    diagnostics.pdfAttachmentsMissingOcr > 0 ||
    diagnostics.pdfAttachmentsStale > 0
  ) {
    return {
      status: workspaceMailText(i18n, 'overview.pdfCheck'),
      detail: formatSearchAttentionSummary(i18n, diagnostics),
    };
  }

  if (diagnostics.messagesMissingBody > 0) {
    return {
      status: workspaceMailText(i18n, 'overview.fulltextCheck'),
      detail: workspaceMailText(i18n, 'overview.messages', {
        count: formatCount(diagnostics.messagesMissingBody),
      }),
    };
  }

  if (diagnostics.pdfAttachmentsRepairable > 0) {
    return {
      status: workspaceMailText(i18n, 'overview.repairable'),
      detail: workspaceMailText(i18n, 'overview.pdfRepairable', {
        count: formatCount(diagnostics.pdfAttachmentsRepairable),
      }),
    };
  }

  return {
    status: formatSearchStatus(i18n, diagnostics.scopeStatus),
    detail: workspaceMailText(i18n, 'overview.noAction'),
  };
}

function formatSearchAttentionSummary(
  i18n: ReturnType<typeof useI18n>,
  diagnostics: MailSearchDiagnostics
): string {
  if (!diagnostics) return workspaceMailText(i18n, 'attention.noDiagnostics');

  const parts: string[] = [];
  if (diagnostics.missingPermissionTargets > 0) {
    parts.push(
      workspaceMailText(i18n, 'attention.missingPermission', {
        count: formatCount(diagnostics.missingPermissionTargets),
      })
    );
  }
  if (diagnostics.messagesMissingBody > 0) {
    parts.push(
      workspaceMailText(i18n, 'attention.missingBody', {
        count: formatCount(diagnostics.messagesMissingBody),
      })
    );
  }
  if (diagnostics.pdfAttachmentsMissingOcr > 0) {
    parts.push(
      workspaceMailText(i18n, 'attention.missingOcr', {
        count: formatCount(diagnostics.pdfAttachmentsMissingOcr),
      })
    );
  }
  if (diagnostics.pdfAttachmentsStale > 0) {
    parts.push(
      workspaceMailText(i18n, 'attention.stale', {
        count: formatCount(diagnostics.pdfAttachmentsStale),
      })
    );
  }
  if (diagnostics.pdfAttachmentsFailed > 0) {
    parts.push(
      workspaceMailText(i18n, 'attention.failed', {
        count: formatCount(diagnostics.pdfAttachmentsFailed),
      })
    );
  }

  return parts.length > 0
    ? parts.join(', ')
    : workspaceMailText(i18n, 'search.notices.empty');
}

function countSearchAttentionDiagnostics(
  diagnostics: MailSearchDiagnostics
): number {
  if (!diagnostics) return 0;
  return (
    diagnostics.missingPermissionTargets +
    diagnostics.messagesMissingBody +
    diagnostics.pdfAttachmentsMissingOcr +
    diagnostics.pdfAttachmentsStale +
    diagnostics.pdfAttachmentsFailed
  );
}

function formatPdfIndexSummary(
  i18n: ReturnType<typeof useI18n>,
  diagnostics: MailSearchDiagnostics
): string {
  if (!diagnostics || diagnostics.totalPdfAttachments === 0)
    return workspaceMailText(i18n, 'searchStatus.none');
  const repairable =
    diagnostics.pdfAttachmentsRepairable > 0
      ? workspaceMailText(i18n, 'pdfRepair.repairableSuffix', {
          count: formatCount(diagnostics.pdfAttachmentsRepairable),
        })
      : '';
  return workspaceMailText(i18n, 'pdfRepair.summary', {
    indexed: formatCount(diagnostics.pdfAttachmentsIndexed),
    total: formatCount(diagnostics.totalPdfAttachments),
    repairable,
  });
}

function groupMailAccountsByUser(
  i18n: ReturnType<typeof useI18n>,
  accounts: MailAdminOverview['accounts']
): MailUserTarget[] {
  const grouped = new Map<
    string,
    {
      userName: string | null;
      userEmail: string;
      emailAddresses: string[];
      budgetMaxMb: number;
      accounts: MailAccountOverview[];
    }
  >();
  for (const account of accounts) {
    const current = grouped.get(account.userId) ?? {
      userName: account.userName,
      userEmail: account.userEmail,
      emailAddresses: [],
      budgetMaxMb: bytesToMb(account.userCacheBudgetMaxBytes),
      accounts: [],
    };
    current.emailAddresses.push(account.emailAddress);
    current.accounts.push(account);
    current.budgetMaxMb = Math.max(
      0,
      Math.min(current.budgetMaxMb, bytesToMb(account.userCacheBudgetMaxBytes))
    );
    grouped.set(account.userId, current);
  }
  return Array.from(grouped.entries()).map(([userId, target]) => {
    const label = target.userName || target.userEmail || userId;
    return {
      userId,
      label,
      userEmail: target.userEmail,
      accountDetail: formatAccountListDetail(i18n, target.emailAddresses),
      budgetMaxMb: target.budgetMaxMb,
      accounts: target.accounts,
      latestSyncedAt: latestMailSyncAt(target.accounts),
    };
  });
}

function filterCacheOverrideTargets(
  targets: MailUserTarget[],
  query: string
): MailUserTarget[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return targets;
  return targets.filter(target =>
    [target.label, target.userEmail, target.accountDetail, target.userId].some(
      value => value.toLowerCase().includes(normalized)
    )
  );
}

function formatAccountListDetail(
  i18n: ReturnType<typeof useI18n>,
  emailAddresses: string[]
): string {
  const unique = [...new Set(emailAddresses)];
  const visible = unique.slice(0, 2).join(', ');
  const remaining = unique.length - 2;
  return remaining > 0
    ? workspaceMailText(i18n, 'accountList.more', {
        visible,
        remaining: formatCount(remaining),
      })
    : visible;
}

function bytesToMb(value: string | null | undefined, fallback = 0): number {
  const bytes = Number.parseInt(value ?? '0', 10);
  return Math.round(bytes / BYTES_PER_MB) || fallback;
}

function formatBytesFromString(value: string | null | undefined): string {
  const numeric = Number.parseInt(value ?? '0', 10);
  if (!Number.isFinite(numeric) || numeric <= 0) return '0 MB';
  if (numeric < BYTES_PER_MB) {
    return `${COUNT_FORMATTER.format(Math.round(numeric / BYTES_PER_KB))} KB`;
  }
  return `${COUNT_FORMATTER.format(Math.round(numeric / BYTES_PER_MB))} MB`;
}

function formatMailProvider(providerKind: string): string {
  switch (providerKind) {
    case 'gmail_oauth':
      return 'Gmail';
    case 'microsoft_oauth':
      return 'Microsoft';
    case 'custom_oauth2':
      return 'OAuth2';
    case 'generic_imap_smtp':
      return 'IMAP/SMTP';
    default:
      return providerKind;
  }
}

function searchStatusVariant(
  status: MailSearchIndexStatus
): 'soft' | 'active' | 'error' {
  if (status === 'indexed' || status === 'none') return 'active';
  if (status === 'failed' || status === 'missing_permission') return 'error';
  return 'soft';
}

function formatDiagnosticMessageLabel(
  i18n: ReturnType<typeof useI18n>,
  messageId: string
): string {
  const suffix = messageId.slice(-8);
  return suffix
    ? workspaceMailText(i18n, 'diagnosticMessage.withSuffix', { suffix })
    : workspaceMailText(i18n, 'diagnosticMessage.generic');
}
